import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import TabStack from "../../../components/common/TabStack";
import Menu from "../../../components/tabs/Menu";
import FMContainer from "../../../components/tabs/FMContainer";
import SpinnerLoader from "../../../components/ui/Loader/spinner";
import ConfirmTabClose from "../../../components/ui/Modals/ConfirmTabClose/ConfirmTabClose";

import { MessageTypeConstants } from "../../../services/bot/Message";
import { processSendMessage } from "../../../services/bot/conversation/SendMessage";
import { DotsLoader } from "../../../components/ui/Loader/dotsLoader";
import { handleTabChangeAction, processAndRemoveTab, setTabState } from "../../../state/actions/tabStack";
import { clearBotCallData, clearContentUrl, clearPrintScreenCheck, markAsOldNotifications, removeNotifications } from "../../../state/actions/chats";
import Breadcrumb from "../../../components/common/Breadcrumb";

// Lazy-load heavy tab components — each pulls in large dependencies (mapbox-gl, chart libs, etc.)
const FMTable = lazy(() => import("../../../components/tabs/FMTable"));
const FMForm = lazy(() => import("../../../components/tabs/FMForm"));
const FMCalendar = lazy(() => import("../../../components/tabs/FMCalendar"));
const FMChart = lazy(() => import("../../../components/tabs/FMChart"));
const FMHtml = lazy(() => import("../../../components/tabs/FMHtml"));

// Lazy-load html2canvas — only needed for screenshot functionality
const loadHtml2Canvas = () => import("html2canvas").then(m => m.default);
import FMFilterDrawer from "../../../components/tabs/FMFilter/FMFilterDrawer";
import ConfirmDeleteTabClose from "../../../components/ui/Modals/ModalPopup";
import FMChatInWindow from "../../../components/tabs/FMChatInWindow";
import { handlePromptOnCloseForModal, showTabCloseModal } from "../../../state/actions/modal";
import FMNotificationNewComponent from "../../../components/tabs/FMNotification";
import { withOutInitSocket } from "../../../state/actions/socket";
import useAudio from "../../../utils/AudioElement";


const HomeComponent = ({ isLoading, refreshToken, connectionId }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedConversation, urlContent, printScreen, embedded } = useSelector((state) => state.chats);
  const conversationId = selectedConversation?.conversationId;
  const tabStack = useSelector(state => state.tabStack[conversationId]);
  const tabState = useSelector(state => state.tabStack.tabState);
  const selectedBotDetails = useSelector(state => state.domain.selectedBotDetails);
  const chatInWindowOpen = useSelector((state) => state.chats.chatInWindowOpen);
  const [iframeUrl, setIframeUrl] = useState(null);
  const queryTabHandledRef = useRef("");

  const [components, setComponents] = useState(tabStack?.components ?? []);
  const queryParams = new URLSearchParams(location.search);
  const tabIdFromUrl = queryParams.get("tabId");
  const botIdFromUrl = queryParams.get("botId");
  const selectedTab = tabStack?.selectedTab;
  const confirmTabCloseModal = useSelector((state) => state.modal?.confirmTabCloseModal) || null;
  const authorizationRequest = useSelector((state) => state?.chats?.authorizationRequest);
  const botCallData = useSelector((state) => state?.chats?.botCallData);
  const promptOnCloseForModal = useSelector((state) => state.modal?.promptOnCloseForModal) || null;
  const standardNotiMsg = useSelector((state) => state.chats.standardNotiMsg);
  const notificationsMsgArray = useSelector((state) => state.chats.notificationsMsgArray);
  const ringAction = useSelector((state) => state?.chats?.ringAction);
  const { playRing, pauseRing } = useAudio();

  const onTabChange = (selectedTabId) => {
    dispatch(setTabState(false))
    dispatch(handleTabChangeAction(conversationId, selectedTabId));
  };

  //if getting url in messageType html 
  // useEffect(() => {
  //   if (urlContent) {
  //     window.open(urlContent,
  //        embedded ? "_self" : "_blank"
  //       );
  //     dispatch(clearContentUrl());
  //   }
  // }, [urlContent]);


  useEffect(() => {
    if ((urlContent || botCallData)) {
      if (embedded) {
        setIframeUrl(urlContent);
      } else if (urlContent) {
        window.open(urlContent, "_blank");
      }
      if (botCallData && botCallData.jwt && botCallData.serverUrl && botCallData.meetingName) {

        let botCallUrl = `https://${botCallData.serverUrl}/${botCallData.meetingName}?jwt=${botCallData.jwt}`;
        window.open(botCallUrl, "_blank");
      }

      dispatch(clearContentUrl());
      dispatch(clearBotCallData())
    }
  }, [urlContent, botCallData]);


  //if getting printScreen : true in messageType html options : to take a screenshot of the screen  
  useEffect(() => {
    if (printScreen) {
      takeScreenshot();
    }
  }, [printScreen]);


  const takeScreenshot = async () => {
    try {
      const element = document.getElementById("screenshot-target");
      if (!element) throw new Error("Screenshot element not found");

      // Scroll to the element and wait for rendering
      element.scrollIntoView({ behavior: "instant", block: "start" });
      await new Promise((r) => setTimeout(r, 800)); // wait for charts etc.

      const html2canvas = await loadHtml2Canvas();
      const canvas = await html2canvas(element, {
        useCORS: true,
        scrollY: -window.scrollY,
        scale: window.devicePixelRatio || 2,
        backgroundColor: null,
      });

      const imgData = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = imgData;
      link.download = `screenshot-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      dispatch(clearPrintScreenCheck());
    } catch (err) {
      dispatch(clearPrintScreenCheck());
      console.error("Screenshot error:", err);
    }
  };

  const typeMap = {
    [MessageTypeConstants.MESSAGE_TYPE_CALENDAR]: MessageTypeConstants.MESSAGE_TYPE_TABLE_RESPONSE,
    [MessageTypeConstants.MESSAGE_TYPE_TABLE]: MessageTypeConstants.MESSAGE_TYPE_TABLE_RESPONSE,
    [MessageTypeConstants.MESSAGE_TYPE_MAP]: MessageTypeConstants.MESSAGE_TYPE_TABLE_RESPONSE,
    [MessageTypeConstants.MESSAGE_TYPE_CONTAINER]: MessageTypeConstants.MESSAGE_TYPE_CONTAINER_RESPONSE,
    [MessageTypeConstants.MESSAGE_TYPE_FORM2]: MessageTypeConstants.MESSAGE_TYPE_FORM_RESPONSE,
    [MessageTypeConstants.MESSAGE_TYPE_HTML]: MessageTypeConstants.MESSAGE_TYPE_HTML_RESPONSE,
    [MessageTypeConstants.MESSAGE_TYPE_TIMELINE]: MessageTypeConstants.MESSAGE_TYPE_TIMELINE_RESPONSE,
    [MessageTypeConstants.MESSAGE_TYPE_SURVEY]: MessageTypeConstants.MESSAGE_TYPE_SURVEY_RESPONSE,
    [MessageTypeConstants.MESSAGE_TYPE_DASHBOARD]: MessageTypeConstants.MESSAGE_TYPE_DASHBOARD,
    [MessageTypeConstants.MESSAGE_TYPE_VIDEOPLAYER]: MessageTypeConstants.MESSAGE_TYPE_VIDEOPLAYER_RESPONSE,
  };

  const handleCloseTab = (message) => {
    const { messageType, options = {} } = message;

    if (promptOnCloseForModal) {
      dispatch(showTabCloseModal(message));
      return;
    }

    const sendMessageObj = {
      messageType: typeMap[messageType] || null,
      message: {
        controlId: options.controlId,
        tabId: options.tabId,
        docId: options.docId,
        action: "close",
        ...(messageType === MessageTypeConstants.MESSAGE_TYPE_VIDEOPLAYER && {
          streamingSessionId: options.streamingSessionId,
        }),
      },
    };

    dispatch(setTabState(false))
    processSendMessage(sendMessageObj);
    dispatch(processAndRemoveTab(conversationId, message));
  };


  const handleCloseTabWithModal = (message) => {
    const { messageType, options = {} } = message;

    const sendMessageObj = {
      messageType: typeMap[messageType] || null,
      message: {
        controlId: options.controlId,
        tabId: options.tabId,
        docId: options.docId,
        action: "close",
        ...(messageType === MessageTypeConstants.MESSAGE_TYPE_VIDEOPLAYER && {
          streamingSessionId: options.streamingSessionId,
        }),
      },
    };

    dispatch(setTabState(false))
    processSendMessage(sendMessageObj);
    dispatch(handlePromptOnCloseForModal(false));
    dispatch(processAndRemoveTab(conversationId, message));
  };

  useEffect(() => {
    setComponents(tabStack?.components ?? []);
    setIframeUrl(null); // so that iframe and components won't be rendered in same screen
    const data = components?.find(
      (item) => item.messageType === MessageTypeConstants.MESSAGE_TYPE_MAP
    );
    if (
      tabState &&
      data &&
      (
        selectedBotDetails?.botId === "48344f1ffc867542bfe38" ||
        selectedBotDetails?.botId === "5bf630c7578a93bf7fbc2"
      )
    ) {
      if (data?.options?.tabId) {
        dispatch(handleTabChangeAction(conversationId, data.options.tabId));
      }
    }
  }, [selectedBotDetails, tabStack?.components]);


  useEffect(() => {

    if (!tabIdFromUrl || !conversationId || !tabStack?.components?.length) return;
    if (botIdFromUrl && selectedBotDetails?.botId !== botIdFromUrl) return;


    const requestedTab = tabStack.components.find(
      (item) => String(item?.options?.tabId) === String(tabIdFromUrl)
    );
    if (!requestedTab?.options?.tabId) return;

    const targetTabId = requestedTab.options.tabId;
    const queryTabKey = `${conversationId}:${selectedBotDetails?.botId || ""}:${targetTabId}`;

    if (queryTabHandledRef.current === queryTabKey) return;
    queryTabHandledRef.current = queryTabKey;
    if (String(selectedTab) !== String(targetTabId)) {
    setTimeout(() => {
      dispatch(setTabState(false));
      dispatch(handleTabChangeAction(conversationId, targetTabId));
    }, 2000);
    }
  }, [
    tabIdFromUrl,
    botIdFromUrl,
    conversationId,
    selectedBotDetails?.botId,
    tabStack?.components,
    selectedTab
  ]);
  const renderComponent = (message) => {
    if (!message?.messageType) return <SpinnerLoader />;
    const isModalComponent = components.find((tab) => (tab.messageType === MessageTypeConstants.MESSAGE_TYPE_FORM2) && tab.options?.modal);
    const baseComponent = (() => {
      switch (message.messageType) {
        case MessageTypeConstants.MESSAGE_TYPE_MENU:
          return <Menu {...message} />;
        case MessageTypeConstants.MESSAGE_TYPE_CONTAINER:
          return <FMContainer {...message} />;
        case MessageTypeConstants.MESSAGE_TYPE_FORM2:
          return <FMForm message={message} />;
        case MessageTypeConstants.MESSAGE_TYPE_TABLE:
          return <FMTable message={message} />;
        case MessageTypeConstants.MESSAGE_TYPE_DASHBOARD:
        case 5000:
          return <FMChart {...message} />;
        case MessageTypeConstants.MESSAGE_TYPE_CALENDAR:
          return <FMCalendar {...message} />;
        case MessageTypeConstants.MESSAGE_TYPE_MAP:
          return <FMCalendar {...message} />;
        case MessageTypeConstants.MESSAGE_TYPE_HTML:
          return <FMHtml message={message} />;
        default:
          return <SpinnerLoader />;
      }
    })();

    return (
      <>
        {baseComponent}

        {/* If a modal form is active, render it on top */}
        {isModalComponent &&
          isModalComponent.messageType === MessageTypeConstants.MESSAGE_TYPE_FORM2 && (
            <div className="modal-overlay">
              <FMForm message={isModalComponent} />
            </div>
          )}

        <div
          className={`transition-all duration-300 ease-in-out relative ${chatInWindowOpen
            ? "col-span-4 xl:col-span-4 2xl:col-span-3 flex justify-center align-middle pe-4"
            : "hidden"
            }`}
        >
          <FMChatInWindow refreshToken={refreshToken} connectionId={connectionId} />
        </div>
      </>
    );
  };

  const handleNewClose = () => {
    dispatch(markAsOldNotifications(conversationId))
  };

  const dismissNotifications = (messageIds) => {
    dispatch(removeNotifications(conversationId, messageIds));
  };

  useEffect(() => {
    setTimeout(() => {
      (withOutInitSocket(false))
    }, 2000)
  }, []);

  useEffect(() => {
    if (ringAction === 'ringStart') {
      playRing()
    } else if (ringAction === 'ringStop') {
      pauseRing()
    }
  }, [ringAction, playRing, pauseRing]);

  return (
    <section
      id="screenshot-target"
      className="home-component relative align-center justify-center"
    >
      {isLoading && <DotsLoader />}
      <div className="">
        {!isLoading &&
          <div className="flex flex-col">
            <TabStack
              initialActiveTabId=""
              tabs={components}
              activeTabId={selectedTab}
              onTabChange={onTabChange}
              handleCloseTab={handleCloseTab}
            />

            {components
              .filter((tab) => tab.options?.tabId === selectedTab)
              .map((tab, index) => (
                tab.options?.style == "Breadcrumb" &&
                <Breadcrumb
                  {...tab}
                  conversationId={conversationId}
                  key={index}
                />
              ))}
            <Suspense fallback={<SpinnerLoader />}>
              {components
                .filter((tab) => {
                  return tab.options?.tabId === selectedTab;
                })
                .map((tab, index) => (
                  <div className="tab-label" key={index}>
                    {renderComponent(tab)}
                  </div>
                ))}
            </Suspense>
            {confirmTabCloseModal && (
              <ConfirmTabClose
                confirmTabCloseModal={confirmTabCloseModal}
                handleCloseTab={() => handleCloseTabWithModal(confirmTabCloseModal)}
              />
            )}

            {iframeUrl && (
              <iframe
                src={iframeUrl}
                title="Embedded Content"
                style={{
                  width: "100%",
                  height: "100vh",
                  border: "none"
                }}
              />
            )}

            <FMFilterDrawer conversation={conversationId} />

            {authorizationRequest && (
              <ConfirmDeleteTabClose
                showModal={authorizationRequest}
                confirmDeleteModal={authorizationRequest && authorizationRequest.message}
              />
            )}
            {standardNotiMsg &&
              <FMNotificationNewComponent
                message={standardNotiMsg}
                conversationId={conversationId}
                messageArr={notificationsMsgArray}
                dismissNotifications={dismissNotifications}
                handleNewClose={handleNewClose}
              />
            }
          </div>}
      </div>
    </section>
  );
};
export default HomeComponent;
